import React, { Suspense } from "react"
import { QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { SentryErrorBoundary } from "./sentry"
import { BrowserRouter as Router } from "react-router-dom"
import { usePage } from "@inertiajs/react"
import { UserProvider } from "hooks/use-current-user"
import { PromoCodeProvider } from "hooks/use-promo-code"
import { FormConfig } from "@kit/design/use-form"
import { Features } from "core/features"
import { queryClient } from "core/query-client"
import { request } from "react-rb/request"
import { useFlash } from "react-rb/use-flash"
import { isInertiaPage } from "core/navigation"
import { StaticFeatures } from "core/features/static-features"
import { PresenceTracking } from "core/presence/presence-tracker"
import { InertiaPageHeader } from "./inertia-page-header"
import { InertiaPageFooter } from "./inertia-page-footer"
import { VerticalNavLayout } from "./vertical-nav-layout"

const submit = async (url, options) => {
  const { data } = await request(url, options)
  return data
}

/**
 * AppWrapper component that serves as a persistent layout for both Inertia pages and ReactRB pages.
 *
 * This component is used as the default layout in the Inertia setup, which means
 * it will persist between page navigations, allowing state to be maintained.
 *
 * The providers (QueryClientProvider, Sentry, etc.) will only be instantiated once
 * and will persist across page navigations, improving performance and state management.
 */
export function AppWrapper({ children }) {
  // Show a toast when react-rb/request's response included a flash
  useFlash()

  // Toggle menu display on React pages
  React.useEffect(() => {
    const btn = document.querySelector('[data-target="#main-navbar"]')
    const menu = document.getElementById("main-navbar")

    const toggleMenu = () => {
      menu.classList.toggle("collapse")
    }
    btn && btn.addEventListener("click", toggleMenu)

    return () => {
      btn && btn.removeEventListener("click", toggleMenu)
    }
  }, [])

  return (
    <>
      {isInertiaPage() && <StaticFeatures />}
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} />
        <SentryErrorBoundary>
          <Features>
            <UserProvider>
              <PromoCodeProvider>
                <FormConfig request={submit}>
                  <Router>
                    <PresenceTracking />
                    {isInertiaPage() ? (
                      <InertiaShell>{children}</InertiaShell>
                    ) : (
                      <ReactRbShell>{children}</ReactRbShell>
                    )}
                  </Router>
                </FormConfig>
              </PromoCodeProvider>
            </UserProvider>
          </Features>
        </SentryErrorBoundary>
      </QueryClientProvider>
    </>
  )
}

function InertiaShell({ children }) {
  const { props: { layoutConfig } } = usePage()

  if (layoutConfig?.components?.verticalNav?.visible) {
    return (
      <VerticalNavLayout layoutConfig={layoutConfig}>
        <Suspense fallback={<div />}>{children}</Suspense>
        <InertiaPageFooter />
      </VerticalNavLayout>
    )
  }

  return (
    <>
      <InertiaPageHeader />
      <Suspense fallback={<div />}>{children}</Suspense>
      <InertiaPageFooter />
    </>
  )
}

function ReactRbShell({ children }) {
  // react-rb pages aren't mounted by Inertia, so usePage() isn't available — read from window.app instead.
  const layoutConfig = typeof window !== "undefined" ? window.app?.layoutConfig : null

  if (layoutConfig?.components?.verticalNav?.visible) {
    return (
      <VerticalNavLayout layoutConfig={layoutConfig}>
        <Suspense fallback={<div />}>{children}</Suspense>
      </VerticalNavLayout>
    )
  }

  // Legacy path: ERB renders nav + banners, React just mounts the page.
  return <Suspense fallback={<div />}>{children}</Suspense>
}
